import { useEffect } from "react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BIO_KEY = "ep_biorhythm_dob";
const LAST_NUDGE_KEY = "ep_nudge_last_date";
const PERMISSION_ASKED_KEY = "ep_nudge_permission_asked";
const OPT_OUT_KEY = "ep_nudge_opted_out";
const THRESHOLD = 40;

/**
 * On page load, fetch today's Cosmic Score and — if it's below the
 * turbulence threshold and we haven't nudged today — show a gentle
 * browser Notification pointing at the grounding meditation.
 *
 * Everything is stored in localStorage so we never nudge the same
 * person twice on the same UTC day. Users can opt out permanently
 * via a one-time sonner action button.
 */
export default function CosmicScoreNudge() {
  useEffect(() => {
    // ── guard: not opted-out, has a DOB, browser supports Notification ──
    const dob = (() => { try { return localStorage.getItem(BIO_KEY); } catch { return null; } })();
    if (!dob) return;
    if (typeof window === "undefined" || typeof Notification === "undefined") return;

    let optedOut = false;
    try { optedOut = localStorage.getItem(OPT_OUT_KEY) === "1"; } catch {}
    if (optedOut) return;

    const todayKey = new Date().toISOString().slice(0, 10);
    let lastNudge = null;
    try { lastNudge = localStorage.getItem(LAST_NUDGE_KEY); } catch {}
    if (lastNudge === todayKey) return; // already nudged today

    const controller = new AbortController();
    // Slight delay so the fetch doesn't compete with first paint
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${BACKEND}/api/cosmic-score?dob=${encodeURIComponent(dob)}`,
          { signal: controller.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        const score = Number(data?.score);
        if (!Number.isFinite(score) || score >= THRESHOLD) return;

        // Below threshold — nudge!
        maybeNudge(score, data?.descriptor || "", todayKey);
      } catch { /* silent — a missed nudge is fine */ }
    }, 3500);

    return () => { clearTimeout(timer); controller.abort(); };
  }, []);

  return null;
}


function maybeNudge(score, descriptor, todayKey) {
  // Mark as nudged FIRST so a refresh doesn't fire another one
  try { localStorage.setItem(LAST_NUDGE_KEY, todayKey); } catch {}

  const title = `A gentle nudge · score ${score}`;
  const body = `${descriptor || "The field feels turbulent today"} — a 5-minute grounding might help.`;
  const url = "/almanac?meditation=quick_ground#meditation";

  const permission = Notification.permission;

  const showNotification = () => {
    try {
      const n = new Notification("Earth Pulse", {
        body: `${title}. ${body}`,
        icon: "/icon-192.png",
        badge: "/icon-32.png",
        tag: "cosmic-score-nudge",
        silent: false,
      });
      n.onclick = () => {
        window.focus();
        window.location.href = url;
        n.close();
      };
    } catch {
      // Fallback to in-app toast if the OS blocks notification creation
      inAppNudge(score, descriptor, url);
    }
  };

  if (permission === "granted") {
    showNotification();
    // Mirror as toast so it's never invisible
    inAppNudge(score, descriptor, url);
    return;
  }

  if (permission === "denied") {
    // OS blocks push — show in-app toast only
    inAppNudge(score, descriptor, url);
    return;
  }

  // permission === "default" — ask permission via a soft in-app CTA
  // Never call requestPermission() automatically on page load without user
  // gesture (browsers may auto-deny). Instead, offer the choice via toast.
  let asked = false;
  try { asked = localStorage.getItem(PERMISSION_ASKED_KEY) === "1"; } catch {}

  toast(`Today's cosmic score is ${score}`, {
    description: `${descriptor || "The field feels turbulent today"}. Tap "Ground me" for a 5-minute reset.`,
    duration: 12000,
    action: {
      label: "Ground me",
      onClick: () => {
        window.location.href = url;
        if (!asked) {
          try { localStorage.setItem(PERMISSION_ASKED_KEY, "1"); } catch {}
          Notification.requestPermission().then((p) => {
            if (p === "granted") showNotification();
          }).catch(() => {});
        }
      },
    },
    cancel: {
      label: "Not today",
      onClick: () => {},
    },
  });

  // Second, unobtrusive follow-up toast that lets them turn off nudges forever
  setTimeout(() => {
    toast("Prefer not to be nudged?", {
      description: "You can turn these gentle notifications off any time.",
      duration: 8000,
      action: {
        label: "Turn off",
        onClick: () => {
          try { localStorage.setItem(OPT_OUT_KEY, "1"); } catch {}
          toast.success("Nudges turned off — enjoy the quiet.");
        },
      },
    });
  }, 14000);
}


function inAppNudge(score, descriptor, url) {
  toast(`Today's cosmic score is ${score}`, {
    description: `${descriptor || "Turbulent"} — a grounding meditation might help.`,
    duration: 10000,
    action: {
      label: "Ground me",
      onClick: () => { window.location.href = url; },
    },
  });
}
